import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faBookOpen, faClipboardCheck, faUserGraduate, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../utils/http';

export interface ModeratorCourse {
  learnershipId: string;
  title: string;
  saqaId: string;
  assessmentCount: number;
  gradedSubmissions: number;
  moderatedSubmissions: number;
  pendingModeration: number;
  learnerCount: number;
}

/** Moderator home and "My Courses" list (same view, two routes). */
export default function ModeratorDashboardPage({ title = 'Moderator Dashboard' }: { title?: string }) {
  const { accessToken, user } = useAuth();
  const [courses, setCourses] = useState<ModeratorCourse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      const res = await authFetch<ModeratorCourse[]>(accessToken, '/api/moderator/courses');
      if (res.ok && res.data) setCourses(res.data);
      else setError(res.message || 'Failed to load your courses.');
      setIsLoading(false);
    })();
  }, [accessToken]);

  const totalPending = courses.reduce((s, c) => s + c.pendingModeration, 0);

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">{title}</h1>
        <p className="text-sm text-brand-900/60 mt-1">Welcome, {user?.firstName}. You have read-only access to the courses below.</p>
      </div>

      <div className="p-8 space-y-6">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {!isLoading && !error && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
              <div className="card-surface p-4">
                <p className="text-xs text-brand-900/50">Assigned courses</p>
                <p className="text-2xl font-bold text-brand-950 mt-1">{courses.length}</p>
              </div>
              <div className="card-surface p-4">
                <p className="text-xs text-brand-900/50">Awaiting moderation</p>
                <p className="text-2xl font-bold text-brand-950 mt-1">{totalPending}</p>
              </div>
              <div className="card-surface p-4">
                <p className="text-xs text-brand-900/50">Moderated</p>
                <p className="text-2xl font-bold text-brand-950 mt-1">{courses.reduce((s, c) => s + c.moderatedSubmissions, 0)}</p>
              </div>
            </div>

            {courses.length === 0 ? (
              <div className="card-surface p-12 text-center">
                <FontAwesomeIcon icon={faBookOpen} className="h-12 w-12 text-brand-300 mb-4" />
                <p className="text-brand-900/60">No courses have been assigned to you yet. Ask an administrator to assign you.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {courses.map((c) => (
                  <Link key={c.learnershipId} to={`/moderator/courses/${c.learnershipId}`} className="card-surface p-5 hover:shadow-lg transition-all block">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-bold text-brand-950">{c.title}</h3>
                        <p className="text-xs text-brand-900/50 mt-0.5">SAQA {c.saqaId}</p>
                      </div>
                      <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-brand-900/30 mt-1" />
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
                      <div>
                        <p className="text-xs text-brand-900/50 flex items-center gap-1">
                          <FontAwesomeIcon icon={faUserGraduate} className="h-3 w-3" /> Learners
                        </p>
                        <p className="font-semibold text-brand-950">{c.learnerCount}</p>
                      </div>
                      <div>
                        <p className="text-xs text-brand-900/50 flex items-center gap-1">
                          <FontAwesomeIcon icon={faBookOpen} className="h-3 w-3" /> Assessments
                        </p>
                        <p className="font-semibold text-brand-950">{c.assessmentCount}</p>
                      </div>
                      <div>
                        <p className="text-xs text-brand-900/50 flex items-center gap-1">
                          <FontAwesomeIcon icon={faClipboardCheck} className="h-3 w-3" /> To moderate
                        </p>
                        <p className={`font-semibold ${c.pendingModeration > 0 ? 'text-amber-600' : 'text-brand-950'}`}>{c.pendingModeration}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
